import { computed, DestroyRef, inject } from '@angular/core';
import { patchState, signalStore, withComputed, withMethods, withState } from '@ngrx/signals';
import { rxMethod } from '@ngrx/signals/rxjs-interop';
import { AUTOMATIONS_API } from '@maximizer/actions/data-access';
import { ACTIONS_SHOWN_FLAG, Automation } from '@maximizer/actions/domain';
import {
  AiFeaturesService,
  AIStore,
  CODE_3_AI_USER_DATA,
  CODE_4_HUB_USER_DATA,
  EXCHANGE_CONSENT_SESSIONS_NOT_ENDED,
  ExchangeConsentService,
  IQBoostViewStore,
  IQDataProcessingFeatures,
  SaveQueue,
} from '@maximizer/ai/data-access';
import { AIFeatures, ConfigurationService, ContextService, FeatureService, LocaleService, SessionService } from '@maximizer/core/shared/data-access';
import { CDN_URL, ConfigurationSetting, ConfigurationSettingCodes, OctopusResponseError } from '@maximizer/core/shared/domain';
import { EmbeddedComponent } from '@maximizer/core/shared/ui/components/base';
import { catchError, debounceTime, distinctUntilChanged, forkJoin, map, Observable, of, pipe, switchMap, tap } from 'rxjs';
import { AiHubEventTracker, EventAction, EventJourney } from '../classes/event-tracker';
import { IQBoostInternalCommand } from '../classes/iqboost';
import { DATA_SOURCE_FLAG, DATA_SOURCE_TEXT } from './definitions/data-processing-definitions';
import { TOOL_DEFINITIONS } from './definitions/tool-definitions';
import { AiHubTab, PreferenceSettingChange, ProcessingChoice } from './models/ai-hub.models';
import { buildAnonymizerControl, buildChoiceControl } from './rules/access-control';
import { buildBanner } from './rules/banner';
import { buildPreferenceViewModels, buildToolViewModels } from './rules/view-models';

interface AiHubUserData {
  dataIsSecureExpanded: boolean;
}

interface OptimisticSave<T> {
  key: string;
  read: () => T;
  write: (value: T) => void;
  value: T;
  save$: Observable<boolean>;
  onSaved: () => void;
  onFailed?: () => void;
}

interface SaveHandlers {
  onSaved?: () => void;
  onFailed?: () => void;
}

type AiHubState = {
  status: 'loading' | 'ready' | 'error';
  isAdmin: boolean;
  isAiEnabled: boolean;
  isAiInstalled: boolean;
  orgAnonymizerRequired: boolean;
  userAnonymizerEnabled: boolean;
  orgUseWebData: boolean;
  userUseWebData: boolean;
  trustPanelExpanded: boolean;
  activeTab: AiHubTab;
  saveErrorCount: number;
  automations: readonly Automation[];
  automationsStatus: 'idle' | 'loading' | 'ready' | 'error';
  openSettings: Readonly<Record<string, boolean>>;
  consentGiven: boolean;
  consentBusy: boolean;
  sessionsNotEnded: boolean;
  turnOffFailed: boolean;
  withdrawConfirmOpen: boolean;
};

const initialState: AiHubState = {
  status: 'loading',
  isAdmin: false,
  isAiEnabled: false,
  isAiInstalled: false,
  orgAnonymizerRequired: false,
  userAnonymizerEnabled: false,
  orgUseWebData: false,
  userUseWebData: false,
  trustPanelExpanded: false,
  activeTab: 'general',
  saveErrorCount: 0,
  automations: [],
  automationsStatus: 'idle',
  openSettings: {},
  consentGiven: false,
  consentBusy: false,
  sessionsNotEnded: false,
  turnOffFailed: false,
  withdrawConfirmOpen: false,
};

const HUB_USER_DATA_DEFAULT: AiHubUserData = { dataIsSecureExpanded: false };
const TRUST_PANEL_DEBOUNCE_MS = 400;
const ANONYMIZER_ANIMATION_MS = 1500;
const EXCHANGE_CONSENT_SETTING = 'exchangeConsent';

type WithdrawalResult = 'withdrawn' | 'sessionsNotEnded' | 'failed';

const toggleMode = (enabled: boolean) => (enabled ? 'enabled' : 'disabled');

export const AiHubStore = signalStore(
  withState(initialState),
  withComputed((store) => {
    const aiStore = inject(AIStore);
    const aiFeatures = inject(AiFeaturesService);
    const feature = inject(FeatureService);
    const cdn = inject(CDN_URL);
    const localeService = inject(LocaleService);
    const context = inject(ContextService);
    const isFeatureOn = (key: string, defaultValue: boolean) => feature.isFeatureOn(key, defaultValue);

    return {
      banner: computed(() => buildBanner(store.isAiEnabled(), store.isAdmin())),
      anonymizerControl: computed(() =>
        buildAnonymizerControl({
          iqBoostOn: store.isAiEnabled(),
          isAdmin: store.isAdmin(),
          orgRequired: store.orgAnonymizerRequired(),
          userEnabled: store.userAnonymizerEnabled(),
          flagEnabled: feature.isFeatureOn('core-ai-anonymizer-enabled', true),
        }),
      ),
      dataSourceControl: computed(() =>
        buildChoiceControl({
          iqBoostOn: store.isAiEnabled(),
          isAdmin: store.isAdmin(),
          flagEnabled: feature.isFeatureOn(DATA_SOURCE_FLAG, true),
          orgOpen: store.orgUseWebData(),
          userOpen: store.userUseWebData(),
          text: DATA_SOURCE_TEXT,
        }),
      ),
      tools: computed(() =>
        buildToolViewModels({
          definitions: TOOL_DEFINITIONS,
          group: aiStore.aiUserDataGroup(),
          iqBoostOn: store.isAiEnabled(),
          isSLE: aiFeatures.isSLE(),
          cdn,
          isFeatureOn,
        }),
      ),
      timeFormat: computed(() => context.timeFormat ?? 't'),
      preferencesEnabledByFlag: computed(() => feature.isFeatureOn(ACTIONS_SHOWN_FLAG, false)),
      preferences: computed(() =>
        buildPreferenceViewModels({
          automations: store.automations(),
          isAdmin: store.isAdmin(),
          iqBoostOn: store.isAiEnabled(),
          openSettings: store.openSettings(),
          formatTime: (date) => localeService.formatTimeUserLocale(date),
        }),
      ),
    };
  }),
  withMethods((store) => {
    const aiStore = inject(AIStore);
    const aiFeatures = inject(AiFeaturesService);
    const iqBoostStore = inject(IQBoostViewStore);
    const session = inject(SessionService);
    const configuration = inject(ConfigurationService);
    const tracker = inject(AiHubEventTracker);
    const automationsApi = inject(AUTOMATIONS_API);
    const exchangeConsent = inject(ExchangeConsentService);

    const hubUserDataCodes: ConfigurationSettingCodes = { code3: CODE_3_AI_USER_DATA, code4: CODE_4_HUB_USER_DATA };
    let hubUserDataSetting: ConfigurationSetting | null = null;
    let animationTimer: ReturnType<typeof setTimeout> | undefined;
    const automationSaves = new SaveQueue();

    inject(DestroyRef).onDestroy(() => clearTimeout(animationTimer));

    const trackChange = (metadata: Record<string, string>) =>
      tracker.trackEvent(EventJourney.Action, EventAction.Changed, metadata);

    const trackPreferenceChange = (metadata: Record<string, string>) =>
      tracker.trackEvent(EventJourney.Preferences, EventAction.Changed, metadata);

    const runSave = (save$: Observable<boolean>, handlers: SaveHandlers = {}): void => {
      save$.pipe(catchError(() => of(false))).subscribe((saved) => {
        if (saved) {
          handlers.onSaved?.();
          return;
        }
        handlers.onFailed?.();
        patchState(store, (state) => ({ saveErrorCount: state.saveErrorCount + 1 }));
      });
    };

    const animateAnonymizer = () => {
      const anonymized = store.orgAnonymizerRequired() || store.userAnonymizerEnabled();
      iqBoostStore.setAnonymizedInAnimation({ animate: true, anonymized });
      clearTimeout(animationTimer);
      animationTimer = setTimeout(() => iqBoostStore.setAnonymizedInAnimation({ animate: false, anonymized }), ANONYMIZER_ANIMATION_MS);
    };

    const notifyLauncher = (enabled: boolean) => {
      const launcher = top?.document?.getElementById('ai-iqboost-launcher') as (HTMLElement & EmbeddedComponent) | null;
      launcher?.doAction(IQBoostInternalCommand.SetEnabled, enabled);
    };

    const loadHubUserData = (): Observable<AiHubUserData> =>
      configuration.getSetting(hubUserDataCodes).pipe(
        map((setting) => {
          hubUserDataSetting = setting;
          return setting?.textValue ? (JSON.parse(setting.textValue) as AiHubUserData) : HUB_USER_DATA_DEFAULT;
        }),
        catchError(() => of(HUB_USER_DATA_DEFAULT)),
      );

    const saveHubUserData = (userData: AiHubUserData): Observable<boolean> =>
      (hubUserDataSetting ? of(hubUserDataSetting) : configuration.getSetting(hubUserDataCodes)).pipe(
        switchMap((setting) => configuration.updateSetting(setting?.id ?? null, hubUserDataCodes, JSON.stringify(userData))),
        tap((setting) => (hubUserDataSetting = setting ?? hubUserDataSetting)),
        map((setting) => (setting?.id?.length ?? 0) > 0),
        catchError(() => of(false)),
      );

    const confirmedValues = new Map<string, unknown>();
    const latestCalls = new Map<string, number>();

    const saveOptimistically = <T>({ key, read, write, value, save$, onSaved, onFailed }: OptimisticSave<T>): void => {
      if (!confirmedValues.has(key)) confirmedValues.set(key, read());
      const call = (latestCalls.get(key) ?? 0) + 1;
      latestCalls.set(key, call);
      write(value);
      runSave(save$, {
        onSaved: () => {
          confirmedValues.set(key, value);
          onSaved();
        },
        onFailed: () => {
          if (latestCalls.get(key) === call && confirmedValues.has(key)) write(confirmedValues.get(key) as T);
          onFailed?.();
        },
      });
    };

    const patchProcessingChoice = (choice: ProcessingChoice, open: boolean): void => {
      const patch: Partial<Record<ProcessingChoice, boolean>> = {};
      patch[choice] = open;
      patchState(store, patch);
    };

    const saveDataSourceChoice = (choice: ProcessingChoice, useWebData: boolean): void => {
      const scope = choice === 'orgUseWebData' ? 'organization' : 'user';
      const change: Partial<IQDataProcessingFeatures> = { useWebData };
      saveOptimistically({
        key: choice,
        read: () => store[choice](),
        write: (value) => patchProcessingChoice(choice, value),
        value: useWebData,
        save$: scope === 'organization' ? aiFeatures.setOrganizationProcessingFeatures(change) : aiFeatures.setUserProcessingFeatures(change),
        onSaved: () => trackChange({ setting: 'dataSource', value: useWebData ? 'online' : 'maximizer', scope }),
      });
    };

    const startConsentWork = (): boolean => {
      if (store.consentBusy()) return false;
      patchState(store, { consentBusy: true });
      return true;
    };

    const trackConsent = (consent: boolean) =>
      trackPreferenceChange({ setting: EXCHANGE_CONSENT_SETTING, value: consent ? 'given' : 'withdrawn', scope: 'user' });

    const forgetSaves = (suffix: ':enabled' | ':available'): void => {
      for (const key of confirmedValues.keys()) if (key.endsWith(suffix)) confirmedValues.delete(key);
      for (const key of latestCalls.keys()) if (key.endsWith(suffix)) latestCalls.delete(key);
    };

    const withEveryAutomation = (automations: readonly Automation[], change: Partial<Pick<Automation, 'enabled' | 'available'>>): Automation[] =>
      automations.map((automation) => ({ ...automation, ...change }));

    const turnOffAllAutomations = (): void => {
      automationSaves.enqueue(() => automationsApi.disableAll().pipe(map(() => true))).subscribe((turnedOff) => {
        if (!turnedOff) {
          patchState(store, { turnOffFailed: true, consentBusy: false });
          return;
        }
        forgetSaves(':enabled');
        patchState(store, (state) => ({
          automations: withEveryAutomation(state.automations, { enabled: false }),
          turnOffFailed: false,
          consentBusy: false,
        }));
      });
    };

    const sendWithdrawal = (): Observable<WithdrawalResult> =>
      exchangeConsent.set(false).pipe(
        map((consent): WithdrawalResult => (consent ? 'failed' : 'withdrawn')),
        catchError((error: unknown) =>
          of<WithdrawalResult>(
            error instanceof OctopusResponseError && error.errorCodes.includes(EXCHANGE_CONSENT_SESSIONS_NOT_ENDED) ? 'sessionsNotEnded' : 'failed',
          ),
        ),
      );

    const recordWithdrawal = (result: Exclude<WithdrawalResult, 'failed'>, wasGiven: boolean): void => {
      patchState(store, { consentGiven: false, sessionsNotEnded: result === 'sessionsNotEnded' });
      if (wasGiven) trackConsent(false);
    };

    const withdrawConsent = (turnOff: boolean): void => {
      const wasGiven = store.consentGiven();
      sendWithdrawal().subscribe((result) => {
        if (result === 'failed') {
          patchState(store, (state) => ({ consentBusy: false, saveErrorCount: state.saveErrorCount + 1 }));
          return;
        }
        recordWithdrawal(result, wasGiven);
        if (turnOff) turnOffAllAutomations();
        else patchState(store, { consentBusy: false });
      });
    };

    const enabledKey = (automationId: string) => `automation:${automationId}:enabled`;

    const anyAutomationConfirmedOn = (): boolean =>
      store.automations().some((automation) => {
        const key = enabledKey(automation.id);
        return confirmedValues.has(key) ? confirmedValues.get(key) === true : automation.enabled;
      });

    const consentBeforeTurningOn = (): Observable<boolean> =>
      exchangeConsent.get().pipe(
        switchMap((given) => {
          if (given) return of(true);
          return exchangeConsent.set(true).pipe(tap((consent) => consent && trackConsent(true)));
        }),
        tap((consent) => {
          if (consent) patchState(store, { consentGiven: true, sessionsNotEnded: false, turnOffFailed: false });
        }),
      );

    const retryWhenSessionsNotEnded = (result: WithdrawalResult): Observable<WithdrawalResult> =>
      result === 'sessionsNotEnded' ? sendWithdrawal().pipe(map((retry) => (retry === 'withdrawn' ? retry : result))) : of(result);

    const withdrawalWhenNothingIsOn = (): Observable<boolean> =>
      automationSaves.enqueue(() => {
        if (!store.consentGiven() || anyAutomationConfirmedOn()) return of(true);
        return sendWithdrawal().pipe(
          switchMap(retryWhenSessionsNotEnded),
          tap((result) => {
            if (result !== 'failed') recordWithdrawal(result, true);
          }),
          map((result) => result === 'withdrawn'),
        );
      });

    const withdrawConsentWhenNothingIsOn = (): void => runSave(withdrawalWhenNothingIsOn());

    const findAutomation = (automationId: string) => store.automations().find((automation) => automation.id === automationId);

    const asSaved = (save$: Observable<void>): Observable<boolean> => save$.pipe(map(() => true));

    const saveAutomation = (save$: Observable<void>): Observable<boolean> => automationSaves.enqueue(() => asSaved(save$));

    const patchAutomation = (automationId: string, change: (automation: Automation) => Automation): void =>
      patchState(store, (state) => ({
        automations: state.automations.map((automation) => (automation.id === automationId ? change(automation) : automation)),
      }));

    const makeAutomationsUnavailableToUsers = (): void => {
      if (!store.isAdmin() || !store.preferencesEnabledByFlag()) return;
      runSave(saveAutomation(automationsApi.makeAllUnavailableToOrg()), {
        onSaved: () => {
          forgetSaves(':available');
          patchState(store, (state) => ({ automations: withEveryAutomation(state.automations, { available: false }) }));
        },
      });
    };

    const loadAutomations = (): void => {
      patchState(store, { automationsStatus: 'loading' });
      forkJoin({ automations: automationsApi.list(), consent: exchangeConsent.get() }).subscribe({
        next: ({ automations, consent }) => {
          patchState(store, { automations, consentGiven: consent, automationsStatus: 'ready' });
          if (consent && !automations.some((automation) => automation.enabled)) withdrawalWhenNothingIsOn().subscribe();
          if (store.isAiInstalled() && !store.isAiEnabled() && automations.some((automation) => automation.available)) makeAutomationsUnavailableToUsers();
        },
        error: () => patchState(store, { automationsStatus: 'error' }),
      });
    };

    const withSetting = (automation: Automation, name: string, value: string | undefined): Automation => {
      const values = Object.fromEntries(Object.entries(automation.values).filter(([key]) => key !== name));
      return { ...automation, values: value === undefined ? values : { ...values, [name]: value } };
    };

    const persistTrustPanel = rxMethod<boolean>(
      pipe(
        debounceTime(TRUST_PANEL_DEBOUNCE_MS),
        distinctUntilChanged(),
        switchMap((expanded) => saveHubUserData({ dataIsSecureExpanded: expanded })),
      ),
    );

    return {
      initialize(): void {
        confirmedValues.clear();
        latestCalls.clear();
        patchState(store, { status: 'loading' });
        session
          .getInfo()
          .pipe(
            switchMap((info) => {
              hubUserDataCodes.code2 = info.user.id;
              iqBoostStore.configurationSettingCodesUser().code2 = info.user.id;
              patchState(store, { isAdmin: info.user.roles.administrator });
              return forkJoin({
                config: aiFeatures.getConfiguration(info),
                hubUserData: loadHubUserData(),
                processing: aiFeatures.getProcessingFeatures(),
              });
            }),
          )
          .subscribe({
            next: ({ config, hubUserData, processing }) => {
              if (aiFeatures.hasUnreadableSettings()) {
                patchState(store, { status: 'error' });
                return;
              }
              aiStore.setUserDataGroup(config.aiUserDataGroup);
              patchState(store, {
                status: 'ready',
                isAiEnabled: config.enabled,
                isAiInstalled: config.installed,
                orgAnonymizerRequired: processing.admin.anonymized,
                userAnonymizerEnabled: processing.user.anonymized,
                orgUseWebData: processing.admin.useWebData,
                userUseWebData: processing.user.useWebData,
                trustPanelExpanded: hubUserData.dataIsSecureExpanded,
              });
              if (store.preferencesEnabledByFlag() && ['idle', 'error'].includes(store.automationsStatus())) loadAutomations();
            },
            error: () => patchState(store, { status: 'error' }),
          });
      },

      setAiEnabled(enabled: boolean): void {
        saveOptimistically({
          key: 'isAiEnabled',
          read: () => store.isAiEnabled(),
          write: (value) => patchState(store, { isAiEnabled: value }),
          value: enabled,
          save$: aiFeatures.setEnabled(enabled),
          onSaved: () => {
            tracker.trackEvent(EventJourney.Action, enabled ? EventAction.Enabled : EventAction.Disabled);
            notifyLauncher(enabled);
            if (!enabled) makeAutomationsUnavailableToUsers();
          },
        });
      },

      setOrgAnonymizer(required: boolean): void {
        saveOptimistically({
          key: 'orgAnonymizerRequired',
          read: () => store.orgAnonymizerRequired(),
          write: (value) => patchState(store, { orgAnonymizerRequired: value }),
          value: required,
          save$: aiFeatures.setOrganizationProcessingFeatures({ anonymized: required }),
          onSaved: animateAnonymizer,
        });
      },

      setUserAnonymizer(enabled: boolean): void {
        saveOptimistically({
          key: 'userAnonymizerEnabled',
          read: () => store.userAnonymizerEnabled(),
          write: (value) => patchState(store, { userAnonymizerEnabled: value }),
          value: enabled,
          save$: aiFeatures.setUserProcessingFeatures({ anonymized: enabled }),
          onSaved: animateAnonymizer,
        });
      },

      setOrgDataSource(useWebData: boolean): void {
        saveDataSourceChoice('orgUseWebData', useWebData);
      },

      setUserDataSource(useWebData: boolean): void {
        saveDataSourceChoice('userUseWebData', useWebData);
      },

      setOrgTool(key: keyof AIFeatures, available: boolean): void {
        runSave(aiFeatures.setOrganizationFeature(key, available), {
          onSaved: () => trackChange({ mode: toggleMode(available), feature: key, scope: 'organization' }),
        });
      },

      setUserTool(key: keyof AIFeatures, enabled: boolean): void {
        runSave(aiFeatures.setUserFeature(key, enabled), {
          onSaved: () => trackChange({ mode: toggleMode(enabled), feature: key, scope: 'user' }),
        });
      },

      loadAutomations,

      setOrgPreference(automationId: string, available: boolean): void {
        if (!findAutomation(automationId)) return;
        saveOptimistically({
          key: `automation:${automationId}:available`,
          read: () => findAutomation(automationId)?.available ?? true,
          write: (value) => patchAutomation(automationId, (automation) => ({ ...automation, available: value })),
          value: available,
          save$: saveAutomation(automationsApi.setAvailable(automationId, available)),
          onSaved: () => trackPreferenceChange({ mode: toggleMode(available), preference: automationId, scope: 'organization' }),
        });
      },

      setUserPreference(automationId: string, enabled: boolean): void {
        if (!findAutomation(automationId)) return;
        saveOptimistically({
          key: enabledKey(automationId),
          read: () => findAutomation(automationId)?.enabled ?? false,
          write: (value) => patchAutomation(automationId, (automation) => ({ ...automation, enabled: value })),
          value: enabled,
          save$: automationSaves.enqueue(() =>
            enabled
              ? consentBeforeTurningOn().pipe(switchMap((consented) => (consented ? asSaved(automationsApi.setEnabled(automationId, true)) : of(false))))
              : asSaved(automationsApi.setEnabled(automationId, false)),
          ),
          onSaved: () => {
            trackPreferenceChange({ mode: toggleMode(enabled), preference: automationId, scope: 'user' });
            if (!enabled) withdrawConsentWhenNothingIsOn();
          },
          onFailed: () => {
            if (enabled) withdrawConsentWhenNothingIsOn();
          },
        });
      },

      setPreferenceSetting(automationId: string, change: PreferenceSettingChange): void {
        if (!findAutomation(automationId)) return;
        saveOptimistically<string | undefined>({
          key: `automation:${automationId}:setting:${change.name}`,
          read: () => findAutomation(automationId)?.values[change.name],
          write: (value) => patchAutomation(automationId, (automation) => withSetting(automation, change.name, value)),
          value: change.value,
          save$: saveAutomation(automationsApi.setSetting(automationId, change.name, change.value)),
          onSaved: () => trackPreferenceChange({ preference: automationId, setting: change.name, value: change.value }),
        });
      },

      giveConsent(): void {
        if (!startConsentWork()) return;
        runSave(exchangeConsent.set(true), {
          onSaved: () => {
            patchState(store, { consentGiven: true, sessionsNotEnded: false, turnOffFailed: false, consentBusy: false });
            trackConsent(true);
          },
          onFailed: () => patchState(store, { consentBusy: false }),
        });
      },

      requestWithdrawConsent(): void {
        patchState(store, { withdrawConfirmOpen: true });
      },

      cancelWithdrawConsent(): void {
        patchState(store, { withdrawConfirmOpen: false });
      },

      confirmWithdrawConsent(): void {
        patchState(store, { withdrawConfirmOpen: false });
        if (!startConsentWork()) return;
        withdrawConsent(true);
      },

      retryConsent(): void {
        if (!store.sessionsNotEnded() && !store.turnOffFailed()) return;
        if (!startConsentWork()) return;
        if (store.sessionsNotEnded()) withdrawConsent(store.turnOffFailed());
        else turnOffAllAutomations();
      },

      togglePreferenceSettings(automationId: string): void {
        patchState(store, (state) => ({
          openSettings: { ...state.openSettings, [automationId]: state.openSettings[automationId] === false },
        }));
      },

      setTrustPanelExpanded(expanded: boolean): void {
        patchState(store, { trustPanelExpanded: expanded });
        persistTrustPanel(expanded);
      },

      selectTab(tab: AiHubTab): void {
        patchState(store, { activeTab: tab });
      },
    };
  }),
);

export type AiHubStore = InstanceType<typeof AiHubStore>;
